import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import classnames from "classnames";
import intersection from "lodash/intersection";

import MultiCarousel from "@stadium/ui/src/components/multi-carousel";
import Product from "@sharedComponents/cards/product";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useStoreContext } from "@components/store/provider";
import useSearchAdditionalProductsList from "@hooks/search/use-fetch-additional-products-basic-detail-items";

import {
  getStoreSyncedToES,
  getStoreCloneId,
  getStoreParentId,
  getStoreHideMoreFromThisBrand,
  getStoreHideYouMightAlsoLike,
  getCommonElasticSearchStoreVariables,
  getStoreID,
  getIsBundleShop,
  getDesignedStoreIds,
  getIsLockerShop,
  getIsLegacyLockerShop,
} from "@store/selectors/store";
import { getCountry } from "@store/selectors/country";
import { getMenuAllChildren } from "@store/selectors/menu";
import { getPricelessProductFetchVariables } from "@selectors/product";

import { getAllMenuTaxons } from "@helpers/menu";
import { getCommonGlobalCountryVariables, getLockerShopESQueryVars } from "@helpers/store";

import {
  DETAIL_PAGE_ADDITIONAL_ITEMS_PER_PAGE,
  giftCard,
  swagmagic,
} from "@constants/products/detail";
import { moreFromThisBrand, youMightAlsoLike } from "@constants/widgets";

import styles from "./additional-items.module.scss";

const emptyObj = {};

const AdditionalItemsContainer = ({ product, intent }) => {
  const hideMoreFromThisBrand = useSelector(getStoreHideMoreFromThisBrand);
  const hideYouMightAlsoLike = useSelector(getStoreHideYouMightAlsoLike);
  const country = useSelector(getCountry);
  const brandId = +product.brand?.id;
  const brandNameVisible = !!product.brand?.displayName;
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeCloneId = useSelector(getStoreCloneId);
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const allChildren = useSelector(getMenuAllChildren);
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const { getStoreUpdatedAt } = usePreviewModeContext();
  const isBundleShop = useSelector(getIsBundleShop);
  const designedStoreIds = useSelector(getDesignedStoreIds);
  const isLockerShop = useSelector(getIsLockerShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);

  const { isGlobalCountry, isNewSwagmagicHost } = useStoreContext();

  const depthTwoTaxon = useMemo(() => {
    return product.taxons.find((t) => t.depth === 2);
  }, [product]);

  const { taxonIds, allMenuTaxonIds } = useMemo(() => {
    const { allMenuTaxonIds } = getAllMenuTaxons(allChildren);
    const productTaxonIds = depthTwoTaxon
      ? [+depthTwoTaxon.id]
      : product.taxons.map((taxon) => +taxon.id);
    return { taxonIds: intersection(allMenuTaxonIds, productTaxonIds), allMenuTaxonIds };
  }, [depthTwoTaxon, product, allChildren]);

  const getHideMoreFromThisBrand = () => {
    return hideMoreFromThisBrand || !brandId || product.intent === giftCard || !brandNameVisible;
  };

  const getHideYouMightAlsoLike = () => {
    return hideYouMightAlsoLike || taxonIds.length === 0;
  };

  const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
    country,
    isGlobalCountry,
    countryKeyAttr: "countryCode",
  });

  const variables = useMemo(() => {
    return {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      taxonIds,
      allMenuTaxonIds,
      brandId,
      perPage: DETAIL_PAGE_ADDITIONAL_ITEMS_PER_PAGE,
      productId: +product.id,
      brandPresent: !!brandId && product.intent !== giftCard,
      sortBy: "price:asc",
      skipWarehouse: !syncedToEs && !!storeCloneId,
      hideMoreFromThisBrand: getHideMoreFromThisBrand(),
      hideYouMightAlsoLike: getHideYouMightAlsoLike(),
      withSwag: product.intent === swagmagic,
      storeUpdatedAt: getStoreUpdatedAt(),
      optimizedSearch: isLockerShop,
      showVariantsAsProducts: true,
      includeProductVariants: isBundleShop,
      ...(isBundleShop && { bulkAvailable: false, withOutOfStock: true }),
      ...commonGlobalCountryVariables,
      ...commonESVariables,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
    };
  }, [
    designedStoreIds,
    isLockerShop,
    storeParentId,
    storeId,
    country,
    taxonIds,
    allMenuTaxonIds,
    brandId,
    product,
    syncedToEs,
    storeCloneId,
    commonESVariables,
    getStoreUpdatedAt,
    isBundleShop,
    pricelessVariables,
  ]);

  const {
    fetchedProductsFromBrand,
    fetchedRecommendedProducts,
    loading,
    getAdditionalProductDetails,
    data,
    productsInventory,
  } = useSearchAdditionalProductsList({ skipInventoryFetching: !isBundleShop });

  useEffect(() => {
    if (storeParentId && taxonIds.length > 0)
      getAdditionalProductDetails({
        variables,
      });
  }, [storeParentId, variables]);

  if (loading || !data) return null;

  const renderProducts = ({ label, data }) => {
    const products = data;
    if (products.length > 0)
      return (
        <div className={styles.additionalItemGroup}>
          <div className={styles.storeHeadingGroup}>
            <div className={`${styles.adCol} ${styles.pr0}`}>
              <hr />
            </div>
            <div className={styles.headingTitle}>{label}</div>
            <div className={`${styles.adCol} ${styles.pl0}`}>
              <hr />
            </div>
          </div>

          <div className={styles.pb64}>
            <MultiCarousel
              containerClass={`${styles.itemCarousel} t-cl-1`}
              showDots={true}
              resolutionCountsMapping={{ desktop: 4, mobile: 2 }}
              infinite={true}
            >
              {products.map((product) => {
                const productQtyMapping = productsInventory[product?.id]?.total || emptyObj;

                return (
                  <Product
                    data={product}
                    className=""
                    productTileClass={classnames("white-base-tile", {
                      [styles.productTileV2]: isNewSwagmagicHost,
                    })}
                    productImageClass={styles.productImageBlock}
                    productTileFooterClass={styles.tileFooter}
                    width="304"
                    height="273"
                    key={product.id}
                    infinite={true}
                    source="search"
                    isNextImage={true}
                    unoptimized
                    productQtyMapping={productQtyMapping}
                    showInventoryStatus={isBundleShop}
                    eventLocationAnalytics={{
                      track: true,
                      target: "",
                      eventProperties: {
                        product_id: product.id,
                        category:
                          product.taxons?.find((t) => t.depth === 2)?.name ||
                          product.taxons?.[0]?.name,
                        section_name: label,
                      },
                    }}
                  />
                );
              })}
            </MultiCarousel>
          </div>
        </div>
      );
  };

  const renderProductsBasedOnIntent = ({ type, label, data }) =>
    intent === type && renderProducts({ label: label, data: data });

  return intent ? (
    <>
      {renderProductsBasedOnIntent({
        type: youMightAlsoLike,
        label: "You might also like",
        data: fetchedRecommendedProducts,
      })}
      {renderProductsBasedOnIntent({
        type: moreFromThisBrand,
        label: "More from this brand",
        data: fetchedProductsFromBrand,
      })}
    </>
  ) : (
    <>
      {renderProducts({ label: "You might also like", data: fetchedRecommendedProducts })}
      {renderProducts({ label: "More from this brand", data: fetchedProductsFromBrand })}
    </>
  );
};

export default AdditionalItemsContainer;
